import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
	Component,
	Input,
	OnDestroy,
	OnInit,
	inject,
	output,
	viewChild,
	input,
	InputSignal,
	Signal,
	OutputEmitterRef,
	DestroyRef
} from '@angular/core';
import {
	DataMigrationTypesEnum,
	Directions,
	Languages,
	MappedNotification,
	Notification,
	NotificationOpenModalAction,
	NotificationRedirectType,
	NotificationTypeEnum,
	NotificationTypesModel,
	Redirect,
	RunMigrationUrl,
	SNACK_BAR_CANCELLATION_DURATION,
	ToastService
} from '@core';
import { Direction } from '@angular/cdk/bidi';
import { MatIcon } from '@angular/material/icon';
import { TranslateService } from '@ngx-translate/core';
import { SnackBarService } from '@ui';
import { NotificationSettingsService } from '@app/services/notification-settings/notification-settings.service';
import { NotificationActionsService } from '@app/services/notification-actions/notification-actions.service';
import { MigrationsDataService } from '@app/services/migrations/migrations-data.service';
import { SnackBarIcon, SnackBarParams, SnackBarType } from '@ui/lib/shared-services';
import { tap } from 'rxjs/operators';
import { getNameForLedesFile } from '@app/services/helpers/helpers.helper';
import { Params, Router } from '@angular/router';
import { PassDataService } from '@app/core/services/pass-data/pass-data.service';
import { NgxZendeskWebwidgetService } from '@nitsanzo/ngx-zendesk-webwidget';
import { TaxDocumentsService } from '@app/core/services/tax-documents/tax-documents.service';
import { NotificationService } from '@app/services/notification/notification.service';

export enum SideBarAction {
	Search,
	Create
}

@Component({
	selector: 'lawyal-side-bar',
	templateUrl: './side-bar.component.html',
	styleUrls: ['./side-bar.component.scss'],
	standalone: false
})
export class SideBarComponent implements OnInit, OnDestroy {
	private readonly destroyRef: DestroyRef = inject(DestroyRef);

	private router = inject(Router);
	private passDataService = inject(PassDataService);
	private translateService = inject(TranslateService);
	private snackService = inject(SnackBarService);
	private toastService = inject(ToastService);
	private notificationSettingsService = inject(NotificationSettingsService);
	private notificationActionService = inject(NotificationActionsService);
	private migrationsDataService = inject(MigrationsDataService);
	private ngxZendeskWebwidgetService = inject(NgxZendeskWebwidgetService);
	private taxDocumentsService = inject(TaxDocumentsService);
	private notificationService = inject(NotificationService);

	readonly notificationsButton: Signal<MatIcon> = viewChild<MatIcon>('notificationsButton');

	readonly selectedSideBarAction: InputSignal<SideBarAction> = input<SideBarAction>(undefined);
	readonly disabled: InputSignal<boolean> = input<boolean>(undefined);
	@Input() set notifications(value: Notification[]) {
		// TODO: Move to a separated component.
		if (value && Array.isArray(value)) {
			this.activeNotifications = this.mapNotificationData(value);
			this.calcNotifications();
		}
	}
	@Input() set notificationAdded(value: Notification) {
		// TODO: Move to a separated component.

		if (value) {
			const mappedNotification: MappedNotification = this.notificationService.mapNotification(value);
			if (mappedNotification.type !== NotificationTypeEnum.DmsReport) {
				this.activeNotifications.unshift(mappedNotification);
			}
			if (!value.read && mappedNotification.type !== NotificationTypeEnum.DmsReport) {
				this.notificationsCounter++;
				this.assignHasNewNotifications();
				// TODO: add animation.
				// this.rotateNotificationButton();
			}

			if (value.message?.toast) {
				if (value.message.tax_documents) {
					this.taxDocumentsService.updateTaxInvoiceDocumentsFromNotification(value.message.tax_documents);
				}
				const routerUrl: string = this.router.url;
				if (routerUrl.includes('proforma-invoice/') && value.message?.proforma_invoice) {
					const updatedInvoice: Params = value.message.proforma_invoice;
					this.passDataService.changeData(updatedInvoice);
				}
				this.notificationService.handleNewNotification(mappedNotification);
			} else if (value.message?.modal) {
				this.notificationService.handleNewHiddenNotification(value);
			}
		}
	}
	@Input() set notificationDeleted(value: Notification) {
		// TODO: Move to a separated component.
		if (value) {
			const index = this.activeNotifications.findIndex(item => item.id === value.id);
			if (index > -1) {
				if (!this.activeNotifications[index].read) {
					this.notificationsCounter--;
					this.assignHasNewNotifications();
				}

				this.activeNotifications.splice(index, 1);
			}
		}
	}

	readonly actionClicked: OutputEmitterRef<SideBarAction> = output<SideBarAction>();

	private direction: Direction; // TODO: Move to a separated component.

	public readonly currentLang: string;
	public readonly languages: typeof Languages = Languages;
	public readonly sideBarAction: typeof SideBarAction = SideBarAction;

	public activeNotifications: MappedNotification[] = []; // TODO: Move to a separated component.
	public notificationsCounter: number; // TODO: Move to a separated component.
	public hasNewNotifications: boolean; // TODO: Move to a separated component.

	constructor() {
		this.currentLang = this.translateService.currentLang;
	}

	ngOnInit() {
		this.notificationService.notification$
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe((notification: Notification) => {
				this.decrementCounter();
			});
	}

	openZendeskWebWidget(): void {
		this.ngxZendeskWebwidgetService.zE('webWidget', 'open');
	}

	mapNotificationData(data: Notification[]): MappedNotification[] {
		// TODO: Move to a separated component.
		return data.map(item => this.notificationService.mapNotification(item));
	}

	openSnackBar(notification: Notification): void {
		//TODO: remove after verifying all notifications are handled in the new service.
		// TODO: Move to a separated component.
		let snackText: string = this.getSnackBarTitle(notification, notification.canMigrated);
		let snackTextLink: string = this.getSnackBarTextLink(notification);
		let redirectTo: Redirect;

		if (notification.message.redirect) {
			redirectTo = this.buildRedirectPath(notification);
		}

		this.direction = this.translateService.currentLang === Languages.HE ? Directions.Rtl : Directions.Ltr;
		const snackBarParams: SnackBarParams = {
			text: snackText,
			iconName: this.getSnackBarIcon(notification),
			textLink: snackTextLink,
			link: notification.message.link,
			links: notification.message.links || [],
			redirectPath: redirectTo?.path || '',
			newTab: redirectTo?.newTab,
			showModal: notification.message.modal,
			fileName: this.getNameForFile(notification),
			notificationActualType: notification.actualType,
			dataMigrationType: notification.message.data_migration?.type,
			canMigrated: notification.canMigrated,
			removeTimeStamp: notification.removeTimeStamp
		};
		const snackRef = this.snackService.openSnackBar(
			SnackBarType.GenerateFile,
			snackBarParams,
			SNACK_BAR_CANCELLATION_DURATION
		);
		snackRef
			.onAction()
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe(() => {
				if (!notification.read) {
					notification.read = true;
					this.decrementCounter();
					this.notificationSettingsService.markNotificationAsRead(notification.id);
				}
			});
	}

	runMigrationFile(notification: Notification) {
		// TODO: Move to a separated component.
		const path: string = RunMigrationUrl[DataMigrationTypesEnum[notification.message.data_migration?.type]];
		this.migrationsDataService
			.runMigrationFile(notification.message.data_migration?.id, path)
			.pipe(
				tap(() => {
					const { TITLE: title, MESSAGE: message }: { TITLE: string; MESSAGE: string } = this.translateService.instant(
						'NEW_NOTIFICATIONS.MIGRATION_FILE_IN_PROCESS'
					);
					this.toastService.customInfo(message, title);
				})
			)
			.subscribe();
	}

	private getNameForFile(notification: Notification): string {
		// TODO: Move to a separated component.
		let fileName: string = '';
		let notificationType: NotificationTypesModel = this.notificationActionService.getActualNotificationType(
			notification.type
		);
		switch (notificationType?.type) {
			case NotificationTypeEnum.Ledes:
				fileName = getNameForLedesFile(notification.message.link);
				break;
		}
		return fileName;
	}

	openModalFromSnackBar(notification: Notification) {
		// TODO: Move to a separated component.
		let notificationType: NotificationTypesModel = this.notificationActionService.getActualNotificationType(
			notification.type
		);
		switch (notificationType?.type) {
			case NotificationTypeEnum.MigrationCheckFail:
				const notificationAction: NotificationOpenModalAction = {
					openMigrationModal: true,
					notification: notification
				};
				this.notificationActionService.toggleMigrationModal = notificationAction;
		}
	}

	private getSnackBarIcon(notification: Notification): string {
		// TODO: Move to a separated component.
		let iconName: string = '';
		let notificationType: NotificationTypesModel = this.notificationActionService.getActualNotificationType(
			notification.type
		);
		switch (notificationType?.type) {
			case NotificationTypeEnum.Pdf:
			case NotificationTypeEnum.Excel:
			case NotificationTypeEnum.Bkmv:
			case NotificationTypeEnum.Ini:
				iconName = SnackBarIcon.CloudDownload;
				break;
			default:
				iconName = SnackBarIcon.Info;
		}
		return iconName;
	}

	private getSnackBarTextLink(notification: Notification): string {
		// TODO: Move to a separated component.
		if (notification.type !== NotificationTypeEnum.Migration) {
			return this.translateService.instant('NOTIFICATIONS.HERE');
		}
	}

	private getSnackBarTitle(notification: Notification, canMigrated: boolean): string {
		// TODO: Move to a separated component.
		let snackText: string;
		if (notification.message.redirect === NotificationRedirectType.ProformaInvoicePreview) {
			//TODO check if exist / check if email error exist
			snackText = this.translateService.instant('NOTIFICATIONS.EMAIL_ERROR');
		} else {
			snackText = this.translateService.instant('NOTIFICATIONS.DOCUMENT_READY_TOAST'); //TODO: remove after verifying all notifications are handled in the new service.
		}
		return snackText;
	}

	private buildRedirectPath(notification: Notification): Redirect {
		let path: string = '';
		let newTab: boolean = false;

		switch (notification.message.redirect) {
			case NotificationRedirectType.ProformaInvoicePreview:
				if (notification.message.id) {
					path = `finance-management/proforma-invoice/${notification.message.id}/document`; // TODO: use enum.
					newTab = true;
				}
				break;
		}

		return { path, newTab };
	}

	calcNotifications() {
		// TODO: Move to a separated component.
		this.notificationsCounter = 0;
		this.activeNotifications.forEach(item => {
			if (!item.read) {
				this.notificationsCounter++;
			}
		});

		if (this.notificationsCounter > 0) {
			//   this.rotateNotificationButton();
			// TODO: add animation.
		}
		this.assignHasNewNotifications();
	}

	// TODO: add animation.
	// rotateNotificationButton() {
	//   if (this.notificationButton) {
	//     const notificationHtmlElement = this.notificationButton._elementRef.nativeElement as HTMLElement;
	//     if (notificationHtmlElement.classList.contains('animate__tada')) {
	//       notificationHtmlElement.classList.remove('animate__tada');
	//     }
	//
	//     setTimeout(() => {
	//       notificationHtmlElement.classList.add('animate__animated', 'animate__tada');
	//     }, NOTIFICATION_ICON_DURATION);
	//   }
	// }

	public decrementCounter(): void {
		// TODO: Move to a separated component.
		this.notificationsCounter--;
		this.assignHasNewNotifications();
	}

	onRunMigration(notification: Notification) {
		// TODO: Move to a separated component.
		this.runMigrationFile(notification);
	}

	private assignHasNewNotifications(): void {
		// TODO: Move to a separated component.
		this.hasNewNotifications = this.notificationsCounter > 0;
	}

	public handleActionClicked(action: SideBarAction): void {
		if (!this.disabled()) {
			this.actionClicked.emit(action);
		}
	}

	ngOnDestroy() {
		this.ngxZendeskWebwidgetService.zE('webWidget', 'hide');
		this.ngxZendeskWebwidgetService.zE('webWidget', 'close');
	}
}
